import React, { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { PROFILES } from '../profiles/index';

export default function DownloadScreen({ profile, workbook, validation, onStartOver, onBack }) {
  const profileDef = PROFILES[profile];
  const [showPreview, setShowPreview] = useState(false);

  const downloadXlsx = useCallback(() => {
    if (!workbook) return;
    const filename = `${profileDef.outputPrefix}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(workbook, filename);
  }, [workbook, profileDef]);

  const downloadTxt = useCallback(() => {
    if (!workbook) return;
    const filename = `${profileDef.outputPrefix}_${new Date().toISOString().slice(0, 10)}.txt`;
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const txt = XLSX.utils.sheet_to_csv(sheet, { FS: '\t' });
    const blob = new Blob([txt], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }, [workbook, profileDef]);

  const previewText = workbook
    ? XLSX.utils.sheet_to_csv(workbook.Sheets[workbook.SheetNames[0]], { FS: '\t' })
        .split('\n')
        .slice(0, 15)
        .join('\n')
    : '';

  return (
    <div className="space-y-6">
      <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
        <div className="text-4xl mb-3">✅</div>
        <h2 className="text-xl font-semibold text-green-800">File Ready for Import</h2>
        <p className="text-sm text-green-600 mt-2">
          {profileDef.name} — {validation.summary.totalRows} rows,{' '}
          {validation.summary.totalAccounts} accounts
        </p>
      </div>

      {/* Download options */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button
          onClick={downloadXlsx}
          className="flex items-center gap-4 p-6 bg-white border-2 border-gray-200 rounded-lg hover:border-mv-500 hover:shadow-md transition-all text-left"
        >
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center text-2xl shrink-0">
            📊
          </div>
          <div>
            <h3 className="font-semibold">Download .xlsx</h3>
            <p className="text-sm text-gray-500 mt-1">Excel workbook for MV interface import</p>
          </div>
        </button>

        <button
          onClick={downloadTxt}
          className="flex items-center gap-4 p-6 bg-white border-2 border-gray-200 rounded-lg hover:border-mv-500 hover:shadow-md transition-all text-left"
        >
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-2xl shrink-0">
            📝
          </div>
          <div>
            <h3 className="font-semibold">Download .txt</h3>
            <p className="text-sm text-gray-500 mt-1">Tab-delimited file for server-side import</p>
          </div>
        </button>
      </div>

      {/* Output preview */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => setShowPreview(!showPreview)}
          className="w-full px-5 py-3 flex items-center justify-between text-sm font-medium text-left hover:bg-gray-50"
        >
          <span>Preview output file (first 15 lines)</span>
          <span className="text-gray-400">{showPreview ? '▲' : '▼'}</span>
        </button>
        {showPreview && (
          <pre className="px-5 pb-4 text-xs font-mono text-gray-700 overflow-x-auto bg-gray-50 border-t border-gray-100 leading-relaxed">
            {previewText}
          </pre>
        )}
      </div>

      {/* MV import walkthrough */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h3 className="font-semibold mb-1">Import into Multiview</h3>
        <p className="text-xs text-gray-500 mb-4">
          Control file: <code className="bg-gray-100 px-1 rounded">{profileDef.controlFile}</code>
          {' · '}
          Interface: <code className="bg-gray-100 px-1 rounded">{profileDef.interfaceId}</code>
        </p>
        <ol className="space-y-4">
          {profileDef.importSteps.map((step, i) => (
            <li key={i} className="flex gap-3">
              <span className="shrink-0 w-6 h-6 bg-mv-100 text-mv-700 rounded-full flex items-center justify-center text-xs font-bold">
                {i + 1}
              </span>
              <div>
                <p className="text-sm font-medium text-gray-800">{step.title}</p>
                <p className="text-sm text-gray-500 mt-0.5">{step.detail}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* Tips */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
        <strong>First time?</strong> Run the batch job in <em>Validate Only</em> mode first.
        If errors appear (especially ORA-20001), note the account numbers and update your source file.
        Processed files move to <code className="bg-yellow-100 px-1 rounded text-xs">/interface/processed/</code>.
      </div>

      <div className="flex justify-between">
        <button
          onClick={onBack ?? onStartOver}
          className="px-5 py-2.5 rounded-lg text-sm font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50"
        >
          ← Back to Review
        </button>
        <button
          onClick={onStartOver}
          className="px-5 py-2.5 rounded-lg text-sm font-medium text-mv-700 bg-mv-50 border border-mv-200 hover:bg-mv-100"
        >
          Process Another File
        </button>
      </div>
    </div>
  );
}
